import type { AxiosError } from 'axios'

/**
 * Shape of the backend's standard error response body.
 * Matches the format from xm-server/src/lib/response.ts
 */
export interface ApiErrorResponse {
  success: false
  error: {
    code: string
    message: string
    details?: unknown
    timestamp: string
    requestId: string
  }
}

/**
 * Typed API error that wraps Axios errors with parsed backend error data.
 * Use this instead of `(error as any)` patterns throughout the app.
 */
export class ApiError extends Error {
  public readonly code: string
  public readonly statusCode: number
  public readonly details?: unknown
  public readonly requestId?: string

  constructor(
    message: string,
    code: string,
    statusCode: number,
    details?: unknown,
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.statusCode = statusCode
    this.details = details
    this.requestId = requestId
  }

  /**
   * Create an ApiError from an Axios error response
   */
  static fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiError {
    const data = error.response?.data
    if (data?.error) {
      return new ApiError(
        data.error.message,
        data.error.code,
        error.response?.status ?? 500,
        data.error.details,
        data.error.requestId,
      )
    }
    return new ApiError(
      error.message || 'An unexpected error occurred',
      'UNKNOWN_ERROR',
      error.response?.status ?? 500,
    )
  }

  /**
   * Type guard to check if an unknown value is an ApiError
   */
  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError
  }
}

/**
 * Extract a user-friendly error message from any error type.
 *
 * Handles:
 * - ApiError instances (from our interceptor)
 * - Raw Axios errors (if interceptor was bypassed)
 * - Plain objects with response.data.error.message structure (e.g., test mocks)
 * - Falls back to provided default for all other types (including plain Error objects,
 *   since their messages are typically technical and not user-friendly)
 *
 * @param error - The caught error (unknown type)
 * @param fallback - Default message if extraction fails
 */
export function getApiErrorMessage(error: unknown, fallback: string = 'An unexpected error occurred'): string {
  // Our typed ApiError (from response interceptor)
  if (error instanceof ApiError) {
    return error.message
  }

  // Raw Axios error with backend error response
  if (isAxiosErrorWithResponse(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined
    if (data?.error?.message) {
      return data.error.message
    }
  }

  // Plain object with response.data.error.message structure (e.g., from test mocks or unwrapped errors)
  if (hasResponseErrorMessage(error)) {
    return error.response.data.error.message
  }

  return fallback
}

/**
 * Type guard for Axios errors with a response
 */
function isAxiosErrorWithResponse(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  )
}

/**
 * Type guard for plain objects with response.data.error.message structure
 */
function hasResponseErrorMessage(
  error: unknown,
): error is { response: { data: { error: { message: string } } } } {
  if (typeof error !== 'object' || error === null) return false
  const e = error as Record<string, unknown>
  const response = e.response as Record<string, unknown> | undefined
  const data = response?.data as Record<string, unknown> | undefined
  const err = data?.error as Record<string, unknown> | undefined
  return typeof err?.message === 'string'
}
